// ── Unified WrestlerCard Component ────────────────────────────────────
// Replaces the inline WrestlerCard in RosterTab and (eventually) other places.
// Variants: 'full' (default for Actual Roster), more to come (compact, mini, etc.)

import React from 'react';
import { getClassIcon, getRoleIcon, getXPBadge } from './wrestlerIcons';
import { getWrestlerName, getWrestlerNameUpper } from './wrestlerUtils';

const placeholder = process.env.PUBLIC_URL + '/placeholder.png';
const placeholderTitle = process.env.PUBLIC_URL + '/Placeholder_Title.png';
const renderError = process.env.PUBLIC_URL + '/wrestlers/render_error.png';
const titleRenderError = process.env.PUBLIC_URL + '/titles/title-error-render.png';

const getWrestlerRender = (w) => {
  if (!w) return null;
  if (w.customRender) return w.customRender;
  if (w.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png';
  return null;
};

const getTitleRender = (id) => id ? process.env.PUBLIC_URL + '/titles/' + id + '-render.png' : null;

// Shared onError handler for wrestler renders
const onWrestlerRenderError = (e) => {
  if (e.target.src !== renderError) e.target.src = renderError;
};

// Shared onError handler for title renders
const onTitleRenderError = (e) => {
  if (e.target.src !== titleRenderError) e.target.src = titleRenderError;
};

const RIVALRY_LVL_COLORS = { 1: '#3498db', 2: '#e67e22', 3: '#e74c3c', 4: '#9b59b6' };

// Recruitment-specific status colors/labels
const RECRUIT_STATUS_COLORS = { 'free': '#2ecc71', 'contract': '#3498db', 'free agent': '#f39c12', 'released': '#95a5a6' };
const SYNERGY_GLOW = '0 0 14px 2px rgba(52, 152, 219, 0.55), 0 0 28px rgba(52, 152, 219, 0.25)';
const CHAMPION_GLOW = '0 0 14px 3px rgba(212, 175, 55, 0.65), 0 0 30px rgba(212, 175, 55, 0.3)';
const SPECIALIST_GLOW = '0 0 14px 3px rgba(230, 126, 34, 0.6), 0 0 28px rgba(230, 126, 34, 0.25)';
const getMatchSpecialistIcon = () => process.env.PUBLIC_URL + '/icons/match_specialist.png';

const RECRUIT_STATUS_LABELS = { 'free': 'FREE AGENT', 'contract': 'CONTRACT', 'free agent': 'FREE AGENT', 'released': 'RELEASED' };

// Inline CSS for animations & hover
const WC_STYLES = `
  .wc-card-unified {
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  }
  .wc-card-unified:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.6);
  }
  .wc-action-btn {
    width: 26px; height: 26px;
    border-radius: 6px;
    background: rgba(0,0,0,0.75);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.18);
    color: rgba(255,255,255,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 11px;
    transition: background 0.15s, border-color 0.15s, transform 0.15s;
    padding: 0;
    line-height: 1;
  }
  .wc-action-btn:hover { background: rgba(0,0,0,0.95); transform: scale(1.1); }
  .wc-action-btn.danger:hover { border-color: #e74c3c; color: #e74c3c; }
  .wc-action-btn.warning:hover { border-color: #e67e22; color: #e67e22; }
  .wc-action-btn.purple:hover { border-color: #9b59b6; color: #9b59b6; }
  .wc-action-btn.active-injury { background: rgba(231,76,60,0.4); border-color: #e74c3c; color: #fff; }
  .wc-action-btn.active-suspended { background: rgba(155,89,182,0.4); border-color: #9b59b6; color: #fff; }
`;

export const WrestlerCardStyles = WC_STYLES;

export default function WrestlerCard({
  wrestler,
  brandColor = '#d4af37',
  rivalries = [],
  champions = null,
  roster = [],
  onClick,
  onRelease,
  onInjure,
  onSuspend,
  onSign,           // for recruitment variant
  showActionButtons = true,
  variant = 'roster', // 'roster' | 'recruit' | 'recruitment' | 'match' | 'preview'
  // Match/Preview variant props
  hasSynergy = false,    // when true, blue glow is applied
  isMatchSpecialist = false, // when true, specialist icon is shown
  isChampionGlow = false,   // when true, gold glow border (booking preview)
  isSpecialistGlow = false, // when true, orange glow border (match type specialist)
  matchType = null,      // for tooltip
  size = null,           // optional override: { width, height, renderHeight }
  hideXP = false,        // hide ringLVL badge
  hideStats = false,     // hide footer stats (only show name)
  currentWeek = null,    // current game week, used to compute contract weeks-left badge
}) {
  if (!wrestler) return null;
  if (!wrestler.name && !wrestler.first_name && !wrestler.nick_name) { console.error("[WrestlerCard] wrestler sans nom:", wrestler); }
  const w = { ...wrestler, name: wrestler.name || wrestler.first_name || wrestler.nick_name || '?' };
  const wid = String(w.wrestler_id);
  const isRecruit = variant === 'recruit' || variant === 'recruitment';
  const isMatch = variant === 'match'; // for booking picker (≈155px) and MatchMaker tiles (140x260)
  const isPreview = variant === 'preview'; // for right-side match preview cards (143x330)

  // Rivalry detection (only in roster variant)
  const rivalry = isRecruit ? null : (rivalries || []).find(r =>
    (r.side1 || []).some(id => String(id) === wid) ||
    (r.side2 || []).some(id => String(id) === wid)
  );
  const rivalColor = rivalry ? (RIVALRY_LVL_COLORS[rivalry.level] || '#3498db') : null;

  // Rival names
  let rivalNames = [];
  if (rivalry) {
    const inS1 = (rivalry.side1 || []).some(id => String(id) === wid);
    const rivalIds = inS1 ? (rivalry.side2 || []) : (rivalry.side1 || []);
    rivalNames = rivalIds.map(id => {
      const rw = (roster || []).find(x => String(x.wrestler_id) === String(id));
      if (rw) return getWrestlerName(rw, 'display');
      // Fallback to snapshot
      const otherSnaps = inS1 ? (rivalry.side2Snapshot || []) : (rivalry.side1Snapshot || []);
      const snap = otherSnaps.find(s => String(s.wrestler_id) === String(id));
      return snap ? getWrestlerName(snap, 'display') : null;
    }).filter(Boolean);
  }

  // Champion detection (only in roster variant)
  const champMap = (!isRecruit && champions && champions.champions) || {};
  const champSlots = (!isRecruit && champions && champions.slots) || [];
  let champSlot = null;
  if (!isRecruit) {
    Object.keys(champMap).forEach(slotKey => {
      const val = champMap[slotKey];
      const wrestlers = Array.isArray(val) ? val : (val ? [val] : []);
      const isChamp = wrestlers.some(cw => {
        if (!cw) return false;
        if (typeof cw === 'object') return String(cw.wrestler_id) === wid;
        return String(cw) === wid;
      });
      if (isChamp) {
        champSlot = champSlots.find(s => s.key === slotKey) || { key: slotKey };
      }
    });
  }
  const isChampion = !!champSlot;
  const titleRender = isChampion
    ? ((champSlot.titleOverride && (champSlot.titleOverride.render || getTitleRender(champSlot.titleOverride.id))) || placeholderTitle)
    : null;

  // Status (only in roster variant)
  const isInjured = !isRecruit && w.injured && w.injuryWeeksLeft > 0;
  const isSuspended = !isRecruit && w.suspended && w.suspensionWeeksLeft > 0;
  const isAbsent = !isRecruit && !isInjured && !isSuspended && w.absent && w.suspensionWeeksLeft > 0;

  // Contract weeks-left badge (only for recruited superstars with a contract, roster variant)
  const hasContract = !isRecruit && w.contractEnd != null && currentWeek != null;
  const contractWeeksLeft = hasContract ? (Number(w.contractEnd) - Number(currentWeek)) : null;
  const isLastWeek = !isRecruit && (w.releasing || (contractWeeksLeft !== null && contractWeeksLeft <= 0));
  const showContractBadge = hasContract || (!isRecruit && w.releasing);

  // Top bar color priority: injured > suspended > absent > champion > rival
  const topBarColor = isInjured ? '#e74c3c' : isSuspended ? '#9b59b6' : isAbsent ? '#1abc9c' : isChampion ? '#d4af37' : rivalColor;
  const borderColor = topBarColor ? topBarColor + '44' : 'rgba(255,255,255,0.08)';


  // ── Match variant — used in BOOKING picker and MatchMaker tiles ──
  if (isMatch) {
    const cardW = (size && size.width) || 155;
    const cardH = (size && size.height) || 240;
    const renderH = (size && size.renderHeight) || 230;
    const baseBorder = isChampionGlow ? '#d4af37' : isSpecialistGlow ? '#e67e22' : hasSynergy ? '#3498db' : 'rgba(255,255,255,0.08)';
    const baseGlow = isChampionGlow ? CHAMPION_GLOW : isSpecialistGlow ? SPECIALIST_GLOW : hasSynergy ? SYNERGY_GLOW : '0 4px 16px rgba(0,0,0,0.4)';
    return (
      <div className="wc-card-unified" onClick={onClick}
        style={{
          width: cardW + 'px',
          flexShrink: 0,
          background: 'rgba(8,8,8,0.92)',
          border: '1px solid ' + baseBorder,
          borderRadius: '10px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          cursor: onClick ? 'pointer' : 'default',
          boxShadow: baseGlow,
          position: 'relative',
        }}>
        {/* Champion top-right badge */}
        {isChampion && (
          <div style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            zIndex: 5,
            background: 'linear-gradient(135deg, rgba(212,175,55,0.4), rgba(255,215,0,0.3))',
            border: '1px solid rgba(212,175,55,0.7)',
            color: '#ffd700',
            padding: '3px 7px',
            borderRadius: '5px',
            fontFamily: "'Anton', sans-serif",
            fontSize: '0.55rem',
            letterSpacing: '1px',
            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          }}>👑</div>
        )}
        {/* RingLVL badge — top right (or shifted down when champion) */}
        {!hideXP && (
          <div style={{ position: 'absolute', top: isChampion ? '38px' : '8px', right: '8px', zIndex: 5, transition: 'top 0.25s' }}
            title={'Ring Level (XP): ' + (w.starting_xp || 0)}>
            <div style={{ position: 'relative', width: '32px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <img loading="lazy" src={getXPBadge(w.starting_xp || 0)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }} onError={e => { e.target.style.display = 'none'; }} />
              <span style={{ position: 'relative', zIndex: 1, fontFamily: "'Anton',sans-serif", fontSize: '11px', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,1)', lineHeight: 1 }}>{w.starting_xp || 0}</span>
            </div>
          </div>
        )}
        {/* Render area */}
        <div style={{ height: renderH + 'px', flexShrink: 0, overflow: 'hidden', position: 'relative', background: 'radial-gradient(ellipse at bottom, ' + brandColor + '22 0%, transparent 70%)' }}>
          <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt={getWrestlerName(w, 'display')}
            style={{
              position: 'absolute',
              bottom: '-12%',
              left: '50%',
              transform: 'translateX(-50%)',
              height: (renderH + 50) + 'px',
              width: 'auto',
              objectFit: 'contain',
              objectPosition: 'bottom',
              filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))',
            }} onError={onWrestlerRenderError} />
          {/* Title render (Match Picker style) */}
          {isChampion && titleRender && (
            <img loading="lazy" src={titleRender} alt="Title" onError={onTitleRenderError}
              style={{
                position: 'absolute',
                bottom: '12px',
                left: '50%',
                transform: 'translateX(-50%)',
                height: '38px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 0 14px rgba(212,175,55,0.9)) drop-shadow(0 0 28px rgba(212,175,55,0.6))',
                zIndex: 4,
              }}
              onError={e => { e.target.style.display = 'none'; }} />
          )}
          {/* Rival banner — slim version */}
          {rivalry && rivalNames.length > 0 && (
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: rivalColor + 'cc',
              padding: '3px 6px',
              textAlign: 'center',
              fontFamily: "'Monda',sans-serif",
              fontSize: '0.36rem',
              color: '#fff',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              ⚔ {rivalNames.join(' & ')}
            </div>
          )}
        </div>
        {/* Footer */}
        <div style={{ padding: '8px 10px 10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center', marginBottom: '6px' }}>
            {getWrestlerNameUpper(w, 'display')}
          </div>
          {!hideStats && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', alignItems: 'center', flexWrap: 'wrap' }}>
              <img loading="lazy" src={getClassIcon(w.fight_class)} alt={w.fight_class} title={w.fight_class}
                style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; }} />
              <img loading="lazy" src={getRoleIcon(w.heel)} alt={w.heel ? 'Heel' : 'Face'} title={w.heel ? 'Heel' : 'Face'}
                style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; }} />
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                background: 'rgba(155,89,182,0.15)',
                border: '1px solid rgba(155,89,182,0.4)',
                borderRadius: '4px',
                padding: '2px 6px',
                fontFamily: "'Anton',sans-serif",
                fontSize: '0.55rem',
                color: '#9b59b6',
                letterSpacing: '0.5px',
                lineHeight: 1,
              }} title={'Promo Skill: ' + (w.promo_skill || 1)}>
                🎤 {w.promo_skill || 1}
              </div>
              {/* Match specialist icon */}
              {isMatchSpecialist && (
                <img loading="lazy" src={getMatchSpecialistIcon()} alt="Specialist" title={'Match Specialist' + (matchType ? ': ' + matchType : '')}
                  style={{ width: '20px', height: '20px', objectFit: 'contain', filter: 'drop-shadow(0 0 4px rgba(241,196,15,0.8))' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Preview variant — used in right-side match preview (143x330) ──
  if (isPreview) {
    const cardW = (size && size.width) || 143;
    const cardH = (size && size.height) || 330;
    const renderH = (size && size.renderHeight) || 264;
    const baseBorder = isChampionGlow ? '#d4af37' : isSpecialistGlow ? '#e67e22' : hasSynergy ? '#3498db' : (brandColor + '33');
    const baseGlow = isChampionGlow ? CHAMPION_GLOW : isSpecialistGlow ? SPECIALIST_GLOW : hasSynergy ? SYNERGY_GLOW : 'none';
    return (
      <div className="wc-card-unified"
        style={{
          width: cardW + 'px',
          height: cardH + 'px',
          position: 'relative',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid ' + baseBorder,
          background: 'rgba(0,0,0,0.4)',
          boxShadow: baseGlow,
          flexShrink: 0,
        }}>
        {/* Champion badge top-right */}
        {isChampion && (
          <div style={{
            position: 'absolute',
            top: '6px',
            right: '6px',
            zIndex: 5,
            background: 'linear-gradient(135deg, rgba(212,175,55,0.45), rgba(255,215,0,0.35))',
            border: '1px solid rgba(212,175,55,0.7)',
            color: '#ffd700',
            padding: '3px 6px',
            borderRadius: '4px',
            fontFamily: "'Anton', sans-serif",
            fontSize: '0.5rem',
            letterSpacing: '0.5px',
          }}>👑</div>
        )}
        {/* RingLVL badge — top right */}
        {!hideXP && (
          <div style={{ position: 'absolute', top: isChampion ? '34px' : '6px', right: '6px', zIndex: 5, transition: 'top 0.25s' }}
            title={'Ring Level (XP): ' + (w.starting_xp || 0)}>
            <div style={{ position: 'relative', width: '30px', height: '30px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <img loading="lazy" src={getXPBadge(w.starting_xp || 0)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }} onError={e => { e.target.style.display = 'none'; }} />
              <span style={{ position: 'relative', zIndex: 1, fontFamily: "'Anton',sans-serif", fontSize: '10px', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,1)', lineHeight: 1 }}>{w.starting_xp || 0}</span>
            </div>
          </div>
        )}
        {/* Render area — fills full card height so no hard cut below footer */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(ellipse at bottom, ' + brandColor + '22 0%, transparent 70%)',
        }}>
          <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt={getWrestlerName(w, 'display')}
            style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              height: (cardH + 30) + 'px',
              width: 'auto',
              objectFit: 'contain',
              objectPosition: 'bottom',
              filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))',
            }} onError={onWrestlerRenderError} />
          {/* Title render */}
          {isChampion && titleRender && (
            <img loading="lazy" src={titleRender} alt="Title"
              style={{
                position: 'absolute',
                bottom: '52px',
                left: '50%',
                transform: 'translateX(-50%)',
                height: '42px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'drop-shadow(0 0 14px rgba(212,175,55,0.9)) drop-shadow(0 0 28px rgba(212,175,55,0.6))',
                zIndex: 4,
              }}
              onError={e => { e.target.style.display = 'none'; }} />
          )}
        </div>
        {/* Footer overlay — gradient so render bleeds through, no hard cut */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '20px 6px 8px',
          background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.82) 40%, rgba(0,0,0,0.97) 100%)',
        }}>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.62rem', letterSpacing: '1px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center', marginBottom: '4px' }}>
            {getWrestlerNameUpper(w, 'display')}
          </div>
          {!hideStats && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', alignItems: 'center' }}>
              <img loading="lazy" src={getClassIcon(w?.fight_class)} alt={w?.fight_class} title={w?.fight_class}
                style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; }} />
              <img loading="lazy" src={getRoleIcon(w?.heel)} alt={w?.heel ? 'Heel' : 'Face'} title={w?.heel ? 'Heel' : 'Face'}
                style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; }} />
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                background: 'rgba(155,89,182,0.15)',
                border: '1px solid rgba(155,89,182,0.4)',
                borderRadius: '4px',
                padding: '2px 6px',
                fontFamily: "'Anton',sans-serif",
                fontSize: '0.55rem',
                color: '#9b59b6',
                letterSpacing: '0.5px',
                lineHeight: 1,
              }} title={'Promo Skill: ' + (w?.promo_skill || 1)}>
                🎤 {w?.promo_skill || 1}
              </div>
              {/* Match specialist icon */}
              {isMatchSpecialist && (
                <img loading="lazy" src={getMatchSpecialistIcon()} alt="Specialist" title={'Match Specialist' + (matchType ? ': ' + matchType : '')}
                  style={{ width: '20px', height: '20px', objectFit: 'contain', filter: 'drop-shadow(0 0 4px rgba(241,196,15,0.8))' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render styles
  const dimmed = isInjured || isSuspended || isAbsent;

  // ── Recruitment variant — simpler, no rivalry/champion/injury, with status + sign button ──
  if (isRecruit) {
    const statusColor = RECRUIT_STATUS_COLORS[w.status] || '#888';
    const statusLabel = RECRUIT_STATUS_LABELS[w.status] || (w.status || '').toUpperCase();
    return (
      <div className="wc-card-unified" onClick={onClick}
        style={{
          background: 'rgba(8,8,8,0.92)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '12px',
          overflow: 'hidden',
          position: 'relative',
          cursor: onClick ? 'pointer' : 'default',
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}>
        {/* RingLVL badge — top right */}
        <div style={{ position: 'absolute', top: '10px', right: '8px', zIndex: 5 }}
          title={'Ring Level (XP): ' + (w.starting_xp || 0)}>
          <div style={{ position: 'relative', width: '38px', height: '38px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <img loading="lazy" src={getXPBadge(w.starting_xp || 0)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }} onError={e => { e.target.style.display = 'none'; }} />
            <span style={{ position: 'relative', zIndex: 1, fontFamily: "'Anton',sans-serif", fontSize: '13px', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,1), 0 0 4px rgba(0,0,0,0.8)', lineHeight: 1 }}>{w.starting_xp || 0}</span>
          </div>
        </div>

        {/* Render area */}
        <div style={{
          height: '260px',
          position: 'relative',
          overflow: 'hidden',
          background: 'radial-gradient(ellipse at bottom, ' + brandColor + '22 0%, transparent 70%)',
        }}>
          <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt={getWrestlerName(w, 'display')}
            style={{
              position: 'absolute',
              bottom: '-10%',
              left: '50%',
              transform: 'translateX(-50%)',
              height: '300px',
              width: 'auto',
              objectFit: 'contain',
              objectPosition: 'bottom',
              filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))',
            }} onError={onWrestlerRenderError} />
          {/* Status banner at bottom of render */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'rgba(0,0,0,0.55)',
            padding: '5px 10px',
            textAlign: 'center',
            fontFamily: "'Monda',sans-serif",
            fontSize: '0.46rem',
            color: '#fff',
            letterSpacing: '1.5px',
          }}>
            {statusLabel}
          </div>
        </div>

        {/* Footer — name + stats + sign button */}
        <div style={{
          padding: '10px 14px 12px',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.7))',
          borderTop: '1px solid rgba(255,255,255,0.05)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
        }}>
          <div style={{
            fontFamily: "'Anton',sans-serif",
            fontSize: '0.85rem',
            letterSpacing: '1.5px',
            color: '#fff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'center',
            width: '100%',
          }}>
            {getWrestlerNameUpper(w, 'display')}
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            flexWrap: 'wrap',
          }}>
            {w.fight_class && (
              <img loading="lazy" src={getClassIcon(w.fight_class)} alt={w.fight_class} title={w.fight_class}
                style={{ width: '28px', height: '28px', objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; }} />
            )}
            <img loading="lazy" src={getRoleIcon(w.heel)} alt={w.heel ? 'Heel' : 'Face'} title={w.heel ? 'Heel' : 'Face'}
              style={{ width: '28px', height: '28px', objectFit: 'contain' }}
              onError={e => { e.target.style.display = 'none'; }} />
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              background: 'rgba(155,89,182,0.15)',
              border: '1px solid rgba(155,89,182,0.4)',
              borderRadius: '5px',
              padding: '4px 9px',
              fontFamily: "'Anton',sans-serif",
              fontSize: '0.7rem',
              color: '#9b59b6',
              letterSpacing: '1px',
              lineHeight: 1,
            }} title={'Promo Skill: ' + (w.promo_skill || 1)}>
              🎤 {w.promo_skill || 1}
            </div>
          </div>
          {/* Sign button */}
          {onSign && (
            <button onClick={e => { e.stopPropagation(); onSign(w); }}
              style={{
                marginTop: '4px',
                width: '100%',
                fontFamily: "'Anton',sans-serif",
                fontSize: '0.7rem',
                letterSpacing: '2px',
                background: 'rgba(46,204,113,0.12)',
                border: '1px solid rgba(46,204,113,0.5)',
                color: '#2ecc71',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(46,204,113,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(46,204,113,0.12)'; }}>
              ✓ SIGN
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="wc-card-unified" onClick={onClick}
      style={{
        background: 'rgba(8,8,8,0.92)',
        border: '1px solid ' + borderColor,
        borderRadius: '12px',
        overflow: 'hidden',
        position: 'relative',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
      }}>

      {/* Top status bar */}
      {topBarColor && (
        <div style={{
          height: '4px',
          background: 'linear-gradient(90deg, transparent, ' + topBarColor + ', transparent)',
          flexShrink: 0,
        }} />
      )}

      {/* Action buttons (vertical column on the left) - only in roster variant */}
      {showActionButtons && !isRecruit && (
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '10px',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}>
          {onRelease && (
            <button className="wc-action-btn danger"
              onClick={e => { e.stopPropagation(); onRelease(w); }}
              title="Release wrestler">✕</button>
          )}
          {onInjure && (
            <button className={'wc-action-btn warning' + (isInjured ? ' active-injury' : '')}
              onClick={e => { e.stopPropagation(); onInjure(w); }}
              title={isInjured ? 'Manage injury' : 'Mark as injured'}>
              <img loading="lazy" src={process.env.PUBLIC_URL + '/icons/injury.png'} alt=""
                style={{ width: '14px', height: '14px', objectFit: 'contain', opacity: isInjured ? 1 : 0.6 }}
                onError={e => { e.target.style.display = 'none'; e.target.parentNode.textContent = '🤕'; }} />
            </button>
          )}
          {onSuspend && (
            <button className={'wc-action-btn purple' + (isSuspended ? ' active-suspended' : '')}
              onClick={e => { e.stopPropagation(); onSuspend(w); }}
              title={isSuspended ? 'Manage suspension' : 'Suspend wrestler'}>
              🚫
            </button>
          )}
        </div>
      )}

      {/* Top-right corner: Champion badge (if champion) + RingLVL (XP) */}
      {isChampion && (
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '10px',
          zIndex: 5,
          background: 'linear-gradient(135deg, rgba(212,175,55,0.4), rgba(255,215,0,0.3))',
          border: '1px solid rgba(212,175,55,0.7)',
          color: '#ffd700',
          padding: '6px 10px',
          borderRadius: '7px',
          fontFamily: "'Anton', sans-serif",
          fontSize: '0.7rem',
          letterSpacing: '1.5px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          boxShadow: '0 0 12px rgba(212,175,55,0.4)',
        }}>
          👑 CHAMP
        </div>
      )}

      {/* Ring Level badge — shifts down when champion takes the top spot */}
      <div style={{
        position: 'absolute',
        top: isChampion ? '56px' : '12px',
        right: '10px',
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        transition: 'top 0.25s',
      }} title={'Ring Level (XP): ' + (w.starting_xp || 0)}>
        <div style={{ position: 'relative', width: '30px', height: '30px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <img loading="lazy" src={getXPBadge(w.starting_xp || 0)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none'; }} />
          <span style={{ position: 'relative', zIndex: 1, fontFamily: "'Anton',sans-serif", fontSize: '13px', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.9)', lineHeight: 1 }}>{w.starting_xp || 0}</span>
        </div>
      </div>

      {/* Custom badge */}
      {w.isCustom && (
        <div style={{
          position: 'absolute',
          top: '12px',
          left: '44px',
          zIndex: 5,
          fontFamily: "'Monda',sans-serif",
          fontSize: '0.32rem',
          color: '#d4af37',
          background: 'rgba(212,175,55,0.15)',
          border: '1px solid rgba(212,175,55,0.3)',
          borderRadius: '3px',
          padding: '1px 4px',
          letterSpacing: '1px',
        }}>CUSTOM</div>
      )}

      {/* Contract weeks-left badge */}
      {showContractBadge && (
        <div style={{
          position: 'absolute',
          top: w.isCustom ? '30px' : '12px',
          left: '44px',
          zIndex: 5,
          fontFamily: "'Monda',sans-serif",
          fontSize: '0.32rem',
          color: isLastWeek ? '#e74c3c' : '#d4af37',
          background: isLastWeek ? 'rgba(231,76,60,0.15)' : 'rgba(212,175,55,0.15)',
          border: '1px solid ' + (isLastWeek ? 'rgba(231,76,60,0.3)' : 'rgba(212,175,55,0.3)'),
          borderRadius: '3px',
          padding: '1px 4px',
          letterSpacing: '1px',
          whiteSpace: 'nowrap',
        }}>{isLastWeek ? 'LAST WEEK' : contractWeeksLeft + ' W. LEFT'}</div>
      )}

      {/* Render area */}
      <div style={{
        height: '280px',
        position: 'relative',
        overflow: 'hidden',
        background: 'radial-gradient(ellipse at bottom, ' + brandColor + '22 0%, transparent 70%)',
      }}>
        <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt={getWrestlerName(w, 'display')}
          style={{
            position: 'absolute',
            bottom: '-10%',
            left: '50%',
            transform: 'translateX(-50%)',
            height: '320px',
            width: 'auto',
            objectFit: 'contain',
            objectPosition: 'bottom',
            opacity: dimmed ? 0.5 : 1,
            filter: dimmed ? 'grayscale(0.6) drop-shadow(0 4px 12px rgba(0,0,0,0.6))' : 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))',
          }} onError={onWrestlerRenderError} />

        {/* Title render (Match Picker style) */}
        {isChampion && titleRender && (
          <img loading="lazy" src={titleRender} alt="Title"
            style={{
              position: 'absolute',
              bottom: '32px',
              left: '50%',
              transform: 'translateX(-50%)',
              height: '50px',
              width: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 0 14px rgba(212,175,55,0.9)) drop-shadow(0 0 28px rgba(212,175,55,0.6))',
              zIndex: 4,
            }}
            onError={e => { e.target.style.display = 'none'; }} />
        )}

        {/* Status banner (priority: injured > suspended > rival) */}
        {isInjured && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'rgba(231,76,60,0.85)',
            padding: '6px 10px',
            textAlign: 'center',
            fontFamily: "'Monda',sans-serif",
            fontSize: '0.42rem',
            color: '#fff',
            letterSpacing: '1.5px',
          }}>
            🤕 INJURED — {w.injuryWeeksLeft} WK{w.injuryWeeksLeft > 1 ? 'S' : ''}
          </div>
        )}
        {!isInjured && isSuspended && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'rgba(155,89,182,0.85)',
            padding: '6px 10px',
            textAlign: 'center',
            fontFamily: "'Monda',sans-serif",
            fontSize: '0.42rem',
            color: '#fff',
            letterSpacing: '1.5px',
          }}>
            🚫 SUSPENDED — {w.suspensionWeeksLeft} WK{w.suspensionWeeksLeft > 1 ? 'S' : ''}
          </div>
        )}
        {isAbsent && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'rgba(26,188,156,0.85)',
            padding: '6px 10px',
            textAlign: 'center',
            fontFamily: "'Monda',sans-serif",
            fontSize: '0.42rem',
            color: '#fff',
            letterSpacing: '1.5px',
          }}>
            ⏸ UNAVAILABLE — {w.suspensionWeeksLeft} WK{w.suspensionWeeksLeft > 1 ? 'S' : ''}
          </div>
        )}
        {!isInjured && !isSuspended && !isAbsent && rivalry && rivalNames.length > 0 && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: rivalColor + 'd9',
            padding: '6px 10px',
            textAlign: 'center',
            fontFamily: "'Monda',sans-serif",
            fontSize: '0.4rem',
            color: '#fff',
            letterSpacing: '1px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            ⚔ VS {rivalNames.join(' & ').toUpperCase()} · LVL. {rivalry.level || 1}
          </div>
        )}
      </div>

      {/* Footer — centered */}
      <div style={{
        padding: '10px 14px 12px',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.7))',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
      }}>
        <div style={{
          fontFamily: "'Anton',sans-serif",
          fontSize: '0.85rem',
          letterSpacing: '1.5px',
          color: '#fff',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'center',
          width: '100%',
        }}>
          {getWrestlerNameUpper(w, 'display')}
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}>
          <img loading="lazy" src={getClassIcon(w.fight_class)} alt={w.fight_class}
            title={w.fight_class}
            style={{ width: '28px', height: '28px', objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }} />
          <img loading="lazy" src={getRoleIcon(w.heel)} alt={w.heel ? 'Heel' : 'Face'}
            title={w.heel ? 'Heel' : 'Face'}
            style={{ width: '28px', height: '28px', objectFit: 'contain' }}
            onError={e => { e.target.style.display = 'none'; }} />
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            background: 'rgba(155,89,182,0.15)',
            border: '1px solid rgba(155,89,182,0.4)',
            borderRadius: '5px',
            padding: '4px 9px',
            fontFamily: "'Anton',sans-serif",
            fontSize: '0.7rem',
            color: '#9b59b6',
            letterSpacing: '1px',
            lineHeight: 1,
          }} title={'Promo Skill: ' + (w.promo_skill || 1)}>
            🎤 {w.promo_skill || 1}
          </div>
        </div>
      </div>
    </div>
  );
}
